import React, { useState, useEffect, useMemo } from "react";
import { Button } from "../design/button";
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogDescription,
  DialogTitle,
} from "../design/dialog";
import { Input } from "../design/input";
import {
  Description,
  ErrorMessage,
  Field,
  Fieldset,
  FieldGroup,
  Label,
} from "../design/fieldset";
import { Select } from "../design/select";
import { useVisit } from "../contexts/VisitContext";
import { useNavigate } from "react-router-dom";
import { useSupervisor } from "../contexts/SupervisorContext";
import { useCase } from "../contexts/CaseContext";
import { useLocation } from "../contexts/LocationContext";
import { useLocationDialog } from "../providers/LocationDialogProvider";
import { Timestamp } from "firebase/firestore";
import { Textarea } from "../design/textarea";
import { Switch, SwitchField } from "../design/switch";
import { ServiceType, SERVICE_TYPE_LABELS, PRIMARY_SERVICE_TYPES, Visit } from "@visitproof/types";
import { toLocalDateString, toLocalTimeString } from "../utils/timeString";
import moment from "moment-timezone";
import { Text } from "../design/text";
import {
  CheckCircleIcon,
  FolderIcon,
  CalendarIcon,
  MapPinIcon,
  DocumentTextIcon,
  ClipboardDocumentListIcon,
  PlusIcon,
} from "@heroicons/react/24/outline";

interface Props {
  caseId?: string | null;
  supervisorId?: string | null;
}

interface Errors {
  startDate?: string;
  endDate?: string;
  caseId?: string;
  supervisorId?: string;
  locationId?: string;
  serviceType?: string;
}

type Step = 1 | 2 | 3 | 4 | 5 | 6;

const commonTimezones = [
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "America/Phoenix",
  "America/Anchorage",
  "Pacific/Honolulu",
];

// Step indicator component
const StepIndicator: React.FC<{
  currentStep: Step;
  totalSteps: number;
  stepLabels: string[];
}> = ({ currentStep, totalSteps, stepLabels }) => (
  <div className="mb-6">
    <div className="flex items-center justify-between">
      {Array.from({ length: totalSteps }, (_, i) => i + 1).map((step) => (
        <React.Fragment key={step}>
          <div className="flex flex-col items-center">
            <div
              className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-medium transition-colors ${
                step < currentStep
                  ? "bg-green-500 text-white"
                  : step === currentStep
                    ? "bg-blue-500 text-white"
                    : "bg-zinc-200 text-zinc-500 dark:bg-zinc-700 dark:text-zinc-400"
              }`}
            >
              {step < currentStep ? (
                <CheckCircleIcon className="h-5 w-5" />
              ) : (
                step
              )}
            </div>
            <span
              className={`mt-1 text-xs ${
                step === currentStep
                  ? "font-medium text-zinc-900 dark:text-white"
                  : "text-zinc-500 dark:text-zinc-400"
              }`}
            >
              {stepLabels[step - 1]}
            </span>
          </div>
          {step < totalSteps && (
            <div
              className={`mx-2 h-0.5 flex-1 ${
                step < currentStep
                  ? "bg-green-500"
                  : "bg-zinc-200 dark:bg-zinc-700"
              }`}
            />
          )}
        </React.Fragment>
      ))}
    </div>
  </div>
);

// Section header component
const SectionHeader: React.FC<{
  icon: React.ElementType;
  title: string;
  description?: string;
}> = ({ icon: Icon, title, description }) => (
  <div className="flex items-start gap-3 mb-4">
    <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-blue-50 dark:bg-blue-900/20">
      <Icon className="h-5 w-5 text-blue-600 dark:text-blue-400" />
    </div>
    <div>
      <Text className="font-semibold text-zinc-900 dark:text-zinc-100">
        {title}
      </Text>
      {description && (
        <Text className="text-sm text-zinc-500 dark:text-zinc-400">
          {description}
        </Text>
      )}
    </div>
  </div>
);

const CreateVisitButton: React.FC<Props> = (props) => {
  const { cases } = useCase();
  const { locations } = useLocation();
  const { openCreateDialog: openLocationDialog } = useLocationDialog();
  const { supervisors } = useSupervisor();
  const { createVisit } = useVisit();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [step, setStep] = useState<Step>(1);

  // Form state
  const [startDate, setStartDate] = useState<string>(
    toLocalDateString(new Date()).slice(0, 10)
  );
  const [endDate, setEndDate] = useState<string>(
    toLocalDateString(new Date()).slice(0, 10)
  );
  const [startTime, setStartTime] = useState<string>(
    toLocalTimeString(new Date()).slice(11, 16)
  );
  const [endTime, setEndTime] = useState<string>(
    toLocalTimeString(new Date()).slice(11, 16)
  );
  const [timeZoneId, setTimeZoneId] = useState(moment.tz.guess());
  const [locationId, setLocationId] = useState<string | null>(null);
  const [caseId, setCaseId] = useState<string | null>(props.caseId ?? null);
  const [supervisorId, setSupervisorId] = useState<string | null>(
    props.supervisorId ?? null
  );
  const [serviceType, setServiceType] = useState<ServiceType | "">("");
  const [notes, setNotes] = useState<string>("");
  const [markAsScheduled, setMarkAsScheduled] = useState(false);

  const [errors, setErrors] = useState<Errors>({});
  const [isSaving, setIsSaving] = useState(false);
  const [caseSearch, setCaseSearch] = useState("");

  // Filter locations when case changes
  const filteredLocations = locations.filter(
    (location) => location.caseId === caseId
  );

  // Filter cases based on search
  const filteredCases = useMemo(() => {
    const activeCases = cases.filter((c) => c.status !== "closed");
    if (!caseSearch.trim()) {
      return activeCases;
    }
    const searchLower = caseSearch.toLowerCase();
    return activeCases.filter((caseItem) => {
      const identifier = caseItem.identifier?.toLowerCase() ?? "";
      const parentNames = Object.values(caseItem.parents ?? {})
        .map((p) => `${p.firstName} ${p.lastName}`.toLowerCase())
        .join(" ");
      const childNames = Object.values(caseItem.children ?? {})
        .map((c) => `${c.firstName} ${c.lastName}`.toLowerCase())
        .join(" ");
      return (
        identifier.includes(searchLower) ||
        parentNames.includes(searchLower) ||
        childNames.includes(searchLower)
      );
    });
  }, [cases, caseSearch]);

  // Get available service types from the selected case
  const selectedCase = cases.find((c) => c.id === caseId);
  const availableServiceTypes = useMemo(() => {
    // Use serviceTypes array, falling back to legacy singular serviceType
    const types = selectedCase?.serviceTypes?.length
      ? selectedCase.serviceTypes
      : selectedCase?.serviceType
        ? [selectedCase.serviceType]
        : [];

    if (types.length === 0) {
      // If case has no service types configured, show primary types
      return PRIMARY_SERVICE_TYPES;
    }
    return types;
  }, [selectedCase]);

  // Reset location and service type when case changes
  useEffect(() => {
    setLocationId(null);
    setServiceType("");
  }, [caseId]);

  // Auto-select service type if only one is available
  useEffect(() => {
    if (availableServiceTypes.length === 1) {
      setServiceType(availableServiceTypes[0]);
    }
  }, [availableServiceTypes]);

  const validateStep = (currentStep: Step): boolean => {
    const newErrors: Errors = {};

    if (currentStep === 1) {
      if (!caseId) {
        newErrors.caseId = "Please select a case.";
      }
    }

    if (currentStep === 2) {
      if (!serviceType) {
        newErrors.serviceType = "Please select a service type.";
      }
    }

    if (currentStep === 3) {
      if (!startDate.trim()) {
        newErrors.startDate = "Start date is required.";
      }
      if (!endDate.trim()) {
        newErrors.endDate = "End date is required.";
      }
      if (startDate && endDate && startTime && endTime) {
        const start = new Date(`${startDate}T${startTime}`);
        const end = new Date(`${endDate}T${endTime}`);
        if (start >= end) {
          newErrors.endDate = "End date/time must be after start date/time.";
        }
      }
    }

    if (currentStep === 4) {
      if (!locationId) {
        newErrors.locationId = "Please select a location.";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(step)) {
      setStep((prev) => Math.min(prev + 1, 6) as Step);
    }
  };

  const handleBack = () => {
    setStep((prev) => Math.max(prev - 1, 1) as Step);
  };

  const handleSubmit = async () => {
    if (!validateStep(step)) {
      return;
    }
    setIsSaving(true);
    try {
      const caseItem = cases.find((caseItem) => caseItem.id === caseId);

      let data = {
        status: markAsScheduled ? "scheduled" : "pending",
        caseId: caseId!,
        startDate: Timestamp.fromDate(new Date(`${startDate}T${startTime}`)),
        endDate: Timestamp.fromDate(new Date(`${endDate}T${endTime}`)),
        parents: caseItem?.parents,
        parentIds: caseItem?.parentIds,
        children: caseItem?.children,
        childIds: caseItem?.childIds,
        notes,
        timeZoneId,
        serviceType: serviceType || undefined,
      } as Visit;

      if (supervisorId) {
        data = {
          ...data,
          supervisorId: supervisorId!,
          supervisor: supervisors.find(
            (supervisor) => supervisor.id === supervisorId
          )!,
        };
      }
      if (locationId) {
        data = {
          ...data,
          locationId: locationId!,
          location: locations.find((location) => location.id === locationId)!,
        };
      }

      const visitId = await createVisit(data);
      navigate(`/visit/${visitId}`);
    } catch (e) {
      console.error("Error creating visit:", e);
    } finally {
      setIsSaving(false);
    }
  };

  const clearForm = () => {
    setStartDate(toLocalDateString(new Date()).slice(0, 10));
    setEndDate(toLocalDateString(new Date()).slice(0, 10));
    setStartTime(toLocalTimeString(new Date()).slice(11, 16));
    setEndTime(toLocalTimeString(new Date()).slice(11, 16));
    setTimeZoneId(moment.tz.guess());
    setCaseId(props.caseId ?? null);
    setSupervisorId(props.supervisorId ?? null);
    setLocationId(null);
    setServiceType("");
    setNotes("");
    setMarkAsScheduled(false);
    setErrors({});
    setStep(1);
    setIsOpen(false);
    setCaseSearch("");
  };

  // Format timezone for display
  const formatTimezone = (tz: string) => {
    const offset = moment.tz(tz).format("Z");
    const abbr = moment.tz(tz).format("z");
    return `${tz.replace(/_/g, " ")} (${abbr}, UTC${offset})`;
  };

  const stepLabels = ["Case", "Service", "Date & Time", "Location", "Monitor", "Review"];

  return (
    <>
      <Button type="button" onClick={() => setIsOpen(true)}>
        Create Visit
      </Button>
      <Dialog open={isOpen} onClose={clearForm} size="2xl">
        <DialogTitle className="flex items-center gap-3">
          {step === 1 && (
            <>
              <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-blue-50 dark:bg-blue-900/20">
                <FolderIcon className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              </div>
              Select Case
            </>
          )}
          {step === 2 && (
            <>
              <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-blue-50 dark:bg-blue-900/20">
                <ClipboardDocumentListIcon className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              </div>
              Choose Service Type
            </>
          )}
          {step === 3 && (
            <>
              <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-blue-50 dark:bg-blue-900/20">
                <CalendarIcon className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              </div>
              Set Date & Time
            </>
          )}
          {step === 4 && (
            <>
              <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-blue-50 dark:bg-blue-900/20">
                <MapPinIcon className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              </div>
              Select Location
            </>
          )}
          {step === 5 && (
            <>
              <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-blue-50 dark:bg-blue-900/20">
                <MapPinIcon className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              </div>
              Assign Monitor
            </>
          )}
          {step === 6 && (
            <>
              <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-blue-50 dark:bg-blue-900/20">
                <DocumentTextIcon className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              </div>
              Review & Confirm
            </>
          )}
        </DialogTitle>
        <DialogDescription>
          {step === 1 && "Select the case for this visit"}
          {step === 2 && "Choose the type of service being provided"}
          {step === 3 && "Choose when the visit will take place"}
          {step === 4 && "Select where the visit will take place"}
          {step === 5 && "Optionally assign a supervisor to monitor this visit"}
          {step === 6 && "Review visit details and confirm"}
        </DialogDescription>
        <DialogBody>
          <StepIndicator
            currentStep={step}
            totalSteps={6}
            stepLabels={stepLabels}
          />

          {/* Step 1: Case Selection */}
          {step === 1 && (
            <div className="space-y-6">
              <div className="space-y-4">
                {/* Search Input */}
                <Field>
                  <Label htmlFor="caseSearch">Search Cases</Label>
                  <Input
                    id="caseSearch"
                    type="text"
                    placeholder="Search by case ID, parent name, or child name..."
                    value={caseSearch}
                    onChange={(e) => setCaseSearch(e.target.value)}
                    disabled={isSaving}
                  />
                </Field>

                {errors.caseId && (
                  <ErrorMessage>{errors.caseId}</ErrorMessage>
                )}

                {/* Case List */}
                <div className="max-h-80 space-y-2 overflow-y-auto rounded-lg border border-zinc-200 p-2 dark:border-zinc-700">
                  {filteredCases.length === 0 ? (
                    <div className="py-8 text-center text-sm text-zinc-500 dark:text-zinc-400">
                      {caseSearch
                        ? "No cases found matching your search."
                        : "No active cases available."}
                    </div>
                  ) : (
                    filteredCases.map((caseItem) => {
                      const isSelected = caseId === caseItem.id;
                      const parentNames = Object.values(caseItem.parents ?? {})
                        .map((p) => `${p.firstName} ${p.lastName}`)
                        .join(", ");
                      const childNames = Object.values(caseItem.children ?? {})
                        .map((c) => c.firstName)
                        .join(", ");

                      return (
                        <button
                          key={caseItem.id}
                          type="button"
                          onClick={() => setCaseId(caseItem.id!)}
                          className={`w-full rounded-lg border-2 p-3 text-left transition-all ${
                            isSelected
                              ? "border-blue-500 bg-blue-50 dark:border-blue-400 dark:bg-blue-900/20"
                              : "border-transparent bg-zinc-50 hover:border-zinc-300 hover:bg-zinc-100 dark:bg-zinc-800/50 dark:hover:border-zinc-600 dark:hover:bg-zinc-800"
                          }`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div
                                className={`font-medium ${
                                  isSelected
                                    ? "text-blue-700 dark:text-blue-300"
                                    : "text-zinc-900 dark:text-white"
                                }`}
                              >
                                {caseItem.identifier ?? "Unnamed Case"}
                              </div>
                              {parentNames && (
                                <div className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">
                                  Parents: {parentNames}
                                </div>
                              )}
                              {childNames && (
                                <div className="text-sm text-zinc-500 dark:text-zinc-400">
                                  Children: {childNames}
                                </div>
                              )}
                              {caseItem.serviceTypes &&
                                caseItem.serviceTypes.length > 0 && (
                                  <div className="mt-1 flex flex-wrap gap-1">
                                    {caseItem.serviceTypes.map((st) => (
                                      <span
                                        key={st}
                                        className="inline-flex items-center rounded-full bg-zinc-200 px-2 py-0.5 text-xs text-zinc-700 dark:bg-zinc-700 dark:text-zinc-300"
                                      >
                                        {SERVICE_TYPE_LABELS[st]}
                                      </span>
                                    ))}
                                  </div>
                                )}
                            </div>
                            {isSelected && (
                              <CheckCircleIcon className="ml-2 h-5 w-5 flex-shrink-0 text-blue-500" />
                            )}
                          </div>
                        </button>
                      );
                    })
                  )}
                </div>

                {/* Selected case count */}
                <div className="text-sm text-zinc-500 dark:text-zinc-400">
                  {filteredCases.length} case{filteredCases.length !== 1 ? "s" : ""} available
                  {caseSearch && ` (filtered)`}
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Service Type Selection */}
          {step === 2 && (
            <div className="space-y-6">
              <Fieldset>
                <FieldGroup>
                  <div>
                    <Text className="mb-3 block text-sm font-medium text-zinc-900 dark:text-zinc-100">
                      Service Type
                    </Text>
                    <Description className="mb-3">
                      {availableServiceTypes.length === Object.keys(SERVICE_TYPE_LABELS).length
                        ? "Select the type of service for this visit"
                        : "Select from the services configured for this case"}
                    </Description>
                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                      {availableServiceTypes.map((type) => {
                        const isSelected = serviceType === type;
                        return (
                          <button
                            key={type}
                            type="button"
                            onClick={() => setServiceType(type)}
                            className={`flex items-center gap-3 rounded-lg border-2 p-3 text-left transition-all ${
                              isSelected
                                ? "border-blue-500 bg-blue-50 dark:border-blue-400 dark:bg-blue-900/20"
                                : "border-zinc-200 bg-white hover:border-zinc-300 dark:border-zinc-700 dark:bg-zinc-900 dark:hover:border-zinc-600"
                            }`}
                          >
                            <ClipboardDocumentListIcon
                              className={`h-5 w-5 ${
                                isSelected
                                  ? "text-blue-600 dark:text-blue-400"
                                  : "text-zinc-400"
                              }`}
                            />
                            <span
                              className={`text-sm font-medium ${
                                isSelected
                                  ? "text-blue-700 dark:text-blue-300"
                                  : "text-zinc-700 dark:text-zinc-300"
                              }`}
                            >
                              {SERVICE_TYPE_LABELS[type]}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                    {errors.serviceType && (
                      <p className="mt-2 text-sm text-red-600 dark:text-red-500">
                        {errors.serviceType}
                      </p>
                    )}
                  </div>
                </FieldGroup>
              </Fieldset>
            </div>
          )}

          {/* Step 3: Date & Time */}
          {step === 3 && (
            <div className="space-y-6">
              <Fieldset>
                <FieldGroup>
                  <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                    <Field>
                      <Label htmlFor="startDate">Start Date</Label>
                      <Input
                        id="startDate"
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        disabled={isSaving}
                        invalid={!!errors.startDate}
                      />
                      {errors.startDate && (
                        <ErrorMessage>{errors.startDate}</ErrorMessage>
                      )}
                    </Field>
                    <Field>
                      <Label htmlFor="startTime">Start Time</Label>
                      <Input
                        id="startTime"
                        type="time"
                        step="900"
                        value={startTime}
                        onChange={(e) => setStartTime(e.target.value)}
                        disabled={isSaving}
                      />
                    </Field>
                  </div>

                  <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                    <Field>
                      <Label htmlFor="endDate">End Date</Label>
                      <Input
                        id="endDate"
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        disabled={isSaving}
                        invalid={!!errors.endDate}
                      />
                      {errors.endDate && (
                        <ErrorMessage>{errors.endDate}</ErrorMessage>
                      )}
                    </Field>
                    <Field>
                      <Label htmlFor="endTime">End Time</Label>
                      <Input
                        id="endTime"
                        type="time"
                        step="900"
                        value={endTime}
                        onChange={(e) => setEndTime(e.target.value)}
                        disabled={isSaving}
                      />
                    </Field>
                  </div>

                  <Field>
                    <Label htmlFor="timeZone">Time Zone</Label>
                    <Select
                      id="timeZone"
                      value={timeZoneId}
                      onChange={(e) => setTimeZoneId(e.target.value)}
                      disabled={isSaving}
                    >
                      <optgroup label="Common Time Zones">
                        {commonTimezones.map((tz) => (
                          <option key={tz} value={tz}>
                            {formatTimezone(tz)}
                          </option>
                        ))}
                      </optgroup>
                      <optgroup label="All Time Zones">
                        {moment.tz
                          .names()
                          .filter((tz) => !commonTimezones.includes(tz))
                          .map((tz) => (
                            <option key={tz} value={tz}>
                              {formatTimezone(tz)}
                            </option>
                          ))}
                      </optgroup>
                    </Select>
                  </Field>
                </FieldGroup>
              </Fieldset>
            </div>
          )}

          {/* Step 4: Location */}
          {step === 4 && (
            <div className="space-y-6">
              <div className="space-y-4">
                <div>
                  <Text className="mb-1 block text-sm font-medium text-zinc-900 dark:text-white">
                    Location
                  </Text>
                  <Text className="mb-3 block text-sm text-zinc-500 dark:text-zinc-400">
                    Select an existing location or create a new one
                  </Text>

                  {errors.locationId && (
                    <ErrorMessage className="mb-2">{errors.locationId}</ErrorMessage>
                  )}

                  {/* Existing locations list */}
                  <div className="max-h-48 space-y-2 overflow-y-auto rounded-lg border border-zinc-200 p-2 dark:border-zinc-700">
                    {filteredLocations.length === 0 ? (
                      <div className="py-4 text-center text-sm text-zinc-500 dark:text-zinc-400">
                        No locations for this case yet.
                      </div>
                    ) : (
                      filteredLocations.map((location) => {
                        const isSelected = locationId === location.id;
                        return (
                          <button
                            key={location.id}
                            type="button"
                            onClick={() => setLocationId(location.id!)}
                            className={`w-full rounded-lg border-2 p-3 text-left transition-all ${
                              isSelected
                                ? "border-blue-500 bg-blue-50 dark:border-blue-400 dark:bg-blue-900/20"
                                : "border-transparent bg-zinc-50 hover:border-zinc-300 hover:bg-zinc-100 dark:bg-zinc-800/50 dark:hover:border-zinc-600 dark:hover:bg-zinc-800"
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <div>
                                <div
                                  className={`font-medium ${
                                    isSelected
                                      ? "text-blue-700 dark:text-blue-300"
                                      : "text-zinc-900 dark:text-white"
                                  }`}
                                >
                                  {location.name}
                                </div>
                                {location.formattedAddress && (
                                  <div className="text-sm text-zinc-500 dark:text-zinc-400">
                                    {location.formattedAddress}
                                  </div>
                                )}
                              </div>
                              {isSelected && (
                                <CheckCircleIcon className="h-5 w-5 flex-shrink-0 text-blue-500" />
                              )}
                            </div>
                          </button>
                        );
                      })
                    )}
                  </div>

                  {/* Add new location button - opens modal */}
                  <button
                    type="button"
                    onClick={() => openLocationDialog({
                      caseId: caseId!,
                      onLocationCreated: (newLocationId) => setLocationId(newLocationId),
                    })}
                    disabled={!caseId}
                    className="mt-3 flex w-full items-center justify-center gap-2 rounded-lg border-2 border-dashed border-zinc-300 p-3 text-sm font-medium text-zinc-600 transition-colors hover:border-blue-400 hover:bg-blue-50 hover:text-blue-600 disabled:cursor-not-allowed disabled:opacity-50 dark:border-zinc-600 dark:text-zinc-400 dark:hover:border-blue-500 dark:hover:bg-blue-900/20 dark:hover:text-blue-400"
                  >
                    <PlusIcon className="h-5 w-5" />
                    Add New Location
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Step 5: Monitor Selection */}
          {step === 5 && (
            <div className="space-y-6">
              <div className="space-y-4">
                <div>
                  <Text className="mb-1 block text-sm font-medium text-zinc-900 dark:text-white">
                    Monitor
                  </Text>
                  <Text className="mb-3 block text-sm text-zinc-500 dark:text-zinc-400">
                    You can assign a monitor now or leave unassigned
                  </Text>

                  {/* Monitor selection as cards */}
                  <div className="max-h-96 space-y-2 overflow-y-auto rounded-lg border border-zinc-200 p-2 dark:border-zinc-700">
                    {/* None option */}
                    <button
                      type="button"
                      onClick={() => setSupervisorId(null)}
                      className={`w-full rounded-lg border-2 p-3 text-left transition-all ${
                        !supervisorId
                          ? "border-blue-500 bg-blue-50 dark:border-blue-400 dark:bg-blue-900/20"
                          : "border-transparent bg-zinc-50 hover:border-zinc-300 hover:bg-zinc-100 dark:bg-zinc-800/50 dark:hover:border-zinc-600 dark:hover:bg-zinc-800"
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <div
                            className={`font-medium ${
                              !supervisorId
                                ? "text-blue-700 dark:text-blue-300"
                                : "text-zinc-900 dark:text-white"
                            }`}
                          >
                            No Monitor Assigned
                          </div>
                          <div className="text-sm text-zinc-500 dark:text-zinc-400">
                            Visit will be unmonitored until a supervisor is assigned
                          </div>
                        </div>
                        {!supervisorId && (
                          <CheckCircleIcon className="h-5 w-5 flex-shrink-0 text-blue-500" />
                        )}
                      </div>
                    </button>

                    {/* Supervisor options */}
                    {supervisors
                      .filter((s) => s.status !== "inactive")
                      .map((supervisor) => {
                        const isSelected = supervisorId === supervisor.id;
                        return (
                          <button
                            key={supervisor.id}
                            type="button"
                            onClick={() => setSupervisorId(supervisor.id!)}
                            className={`w-full rounded-lg border-2 p-3 text-left transition-all ${
                              isSelected
                                ? "border-blue-500 bg-blue-50 dark:border-blue-400 dark:bg-blue-900/20"
                                : "border-transparent bg-zinc-50 hover:border-zinc-300 hover:bg-zinc-100 dark:bg-zinc-800/50 dark:hover:border-zinc-600 dark:hover:bg-zinc-800"
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <div>
                                <div
                                  className={`font-medium ${
                                    isSelected
                                      ? "text-blue-700 dark:text-blue-300"
                                      : "text-zinc-900 dark:text-white"
                                  }`}
                                >
                                  {supervisor.firstName} {supervisor.lastName}
                                </div>
                                {supervisor.email && (
                                  <div className="text-sm text-zinc-500 dark:text-zinc-400">
                                    {supervisor.email}
                                  </div>
                                )}
                              </div>
                              {isSelected && (
                                <CheckCircleIcon className="h-5 w-5 flex-shrink-0 text-blue-500" />
                              )}
                            </div>
                          </button>
                        );
                      })}
                  </div>

                  {supervisors.filter((s) => s.status !== "inactive").length === 0 && (
                    <div className="mt-2 text-sm text-zinc-500 dark:text-zinc-400">
                      No active supervisors available
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Step 6: Review & Notes */}
          {step === 6 && (
            <div className="space-y-6">
              {/* Visit Summary */}
              <div className="rounded-lg border border-zinc-200 bg-zinc-50 p-4 dark:border-zinc-700 dark:bg-zinc-800/50">
                <h4 className="mb-3 text-sm font-semibold text-zinc-900 dark:text-white">
                  Visit Summary
                </h4>
                <dl className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
                  <div>
                    <dt className="text-zinc-500 dark:text-zinc-400">Case</dt>
                    <dd className="font-medium text-zinc-900 dark:text-white">
                      {selectedCase?.identifier ?? "Unnamed Case"}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-zinc-500 dark:text-zinc-400">
                      Service Type
                    </dt>
                    <dd className="font-medium text-zinc-900 dark:text-white">
                      {serviceType ? SERVICE_TYPE_LABELS[serviceType] : "—"}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-zinc-500 dark:text-zinc-400">Date</dt>
                    <dd className="font-medium text-zinc-900 dark:text-white">
                      {startDate === endDate
                        ? new Date(startDate).toLocaleDateString()
                        : `${new Date(startDate).toLocaleDateString()} - ${new Date(endDate).toLocaleDateString()}`}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-zinc-500 dark:text-zinc-400">Time</dt>
                    <dd className="font-medium text-zinc-900 dark:text-white">
                      {startTime} - {endTime}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-zinc-500 dark:text-zinc-400">
                      Location
                    </dt>
                    <dd className="font-medium text-zinc-900 dark:text-white">
                      {locations.find((l) => l.id === locationId)?.name ?? "—"}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-zinc-500 dark:text-zinc-400">
                      Monitor
                    </dt>
                    <dd className="font-medium text-zinc-900 dark:text-white">
                      {supervisors.find((s) => s.id === supervisorId)
                        ? `${supervisors.find((s) => s.id === supervisorId)?.firstName} ${supervisors.find((s) => s.id === supervisorId)?.lastName}`
                        : "Not assigned"}
                    </dd>
                  </div>
                  {selectedCase?.children && (
                    <div className="sm:col-span-2">
                      <dt className="text-zinc-500 dark:text-zinc-400">
                        Children
                      </dt>
                      <dd className="font-medium text-zinc-900 dark:text-white">
                        {Object.values(selectedCase.children)
                          .map((c) => `${c.firstName} ${c.lastName}`)
                          .join(", ")}
                      </dd>
                    </div>
                  )}
                </dl>
              </div>

              {/* Notes */}
              <Fieldset>
                <FieldGroup>
                  <Field>
                    <Label htmlFor="notes">Notes (Optional)</Label>
                    <Description>
                      Add any special instructions or notes for this visit
                    </Description>
                    <Textarea
                      id="notes"
                      value={notes}
                      placeholder="Enter any notes about this visit..."
                      onChange={(e) => setNotes(e.target.value)}
                      disabled={isSaving}
                      rows={3}
                    />
                  </Field>

                  <SwitchField>
                    <Label>Mark as Scheduled</Label>
                    <Description>
                      Enable this to set the visit status to "Scheduled" instead of "Pending"
                    </Description>
                    <Switch
                      checked={markAsScheduled}
                      onChange={setMarkAsScheduled}
                      disabled={isSaving}
                    />
                  </SwitchField>
                </FieldGroup>
              </Fieldset>
            </div>
          )}
        </DialogBody>
        <DialogActions>
          {step > 1 ? (
            <Button plain onClick={handleBack} disabled={isSaving}>
              Back
            </Button>
          ) : (
            <Button plain onClick={clearForm} disabled={isSaving}>
              Cancel
            </Button>
          )}
          {step < 6 ? (
            <Button onClick={handleNext} disabled={isSaving}>
              Continue
            </Button>
          ) : (
            <Button disabled={isSaving} onClick={handleSubmit}>
              {isSaving ? "Creating..." : "Create Visit"}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CreateVisitButton;
